import i18n from '@/utils/i18n';
import React from 'react';
import TrashIcon from '@assets/icons/trash-small.svg';
import {
  CheckIn_DeleteOrderInvoiceItemIssueMutation,
  CheckIn_DeleteOrderInvoiceItemIssueMutationVariables,
  OrderInvoiceCheckInItemStatus,
} from '@/types';
import { createStyles } from '@/contexts/theme-context';
import { FONTS } from '@/styles';
import { gql, useApolloClient, useMutation } from '@apollo/client';
import { Text, TouchableOpacity, View } from 'react-native';
import { useCheckInContext } from '@/contexts/check-in-context';
import { useToasts } from '@/contexts/toast-context';

export const DELETE_ORDER_INVOICE_CHECK_IN_ITEM_ISSUE_MUTATION = gql`
  mutation CheckIn_DeleteOrderInvoiceItemIssue(
    $checkInUUID: uuid!
    $checkInItemIssueUUID: uuid!
  ) {
    deleteOrderInvoiceCheckInItemIssue(
      order_invoice_check_in_id: $checkInUUID
      order_invoice_check_in_item_issue_id: $checkInItemIssueUUID
    ) {
      success
    }
  }
`;

export interface GroupedIssueByStatus {
  status: OrderInvoiceCheckInItemStatus;
  totalQuantity: number;
  uuids: string[];
}

interface Props {
  checkInItemUUID: string;
  checkInUUID: string;
  groupedIssue: GroupedIssueByStatus;
}

export const OrderCheckInIssue: React.FC<Props> = ({
  checkInItemUUID,
  checkInUUID,
  groupedIssue,
}) => {
  const [styles, theme] = useStyles();
  const client = useApolloClient();
  const { refreshOnDeleteIssue } = useCheckInContext();
  const { showErrorToast } = useToasts();

  const [deleteIssue] = useMutation<
    CheckIn_DeleteOrderInvoiceItemIssueMutation,
    CheckIn_DeleteOrderInvoiceItemIssueMutationVariables
  >(DELETE_ORDER_INVOICE_CHECK_IN_ITEM_ISSUE_MUTATION, {
    context: {
      retryEnabled: true,
    },
  });

  const onDeleteGroupedIssue = async () => {
    try {
      const deletePromises = groupedIssue.uuids.map((uuid) =>
        deleteIssue({ variables: { checkInItemIssueUUID: uuid, checkInUUID } }),
      );
      await Promise.all(deletePromises);
      refreshOnDeleteIssue(client, checkInItemUUID, groupedIssue.uuids);
    } catch {
      showErrorToast();
    }
  };

  return (
    <View style={styles.issueWrapper}>
      <Text style={styles.issueDescription}>
        {groupedIssue.status === OrderInvoiceCheckInItemStatus.Other
          ? groupedIssue.totalQuantity > 1
            ? i18n.t('check_in.issue_other_description_plural', {
                count: groupedIssue.totalQuantity,
              })
            : i18n.t('check_in.issue_other_description_singular')
          : groupedIssue.totalQuantity > 1
            ? i18n.t('check_in.issue_description_plural', {
                count: groupedIssue.totalQuantity,
                reason: i18n
                  .t(
                    `check_in.issue_form.reason.${groupedIssue.status.toLowerCase()}`,
                  )
                  .toLowerCase(),
              })
            : i18n.t('check_in.issue_description_singular', {
                count: groupedIssue.totalQuantity,
                reason: i18n
                  .t(
                    `check_in.issue_form.reason.${groupedIssue.status.toLowerCase()}`,
                  )
                  .toLowerCase(),
              })}
      </Text>
      <TouchableOpacity
        onPress={onDeleteGroupedIssue}
        style={styles.deleteButton}
        testID={`delete_issue_${groupedIssue.status}_button`}
      >
        <TrashIcon stroke={theme.sk.color.icon.tertiary} />
      </TouchableOpacity>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  deleteButton: {
    padding: 4,
  },
  issueDescription: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.tertiary,
    fontSize: theme.fontSizes.small,
    lineHeight: 16,
  },
  issueWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
    padding: 4,
  },
}));
